import React, { useMemo } from 'react';
import { Card, Box, Typography } from '@mui/material';
import { COLOR, FUENTE } from './tokens';
import { TIPOS_TRAMITE_PICKER } from '../../../utils/rutasTramite';
import InfoGrafico from './InfoGrafico';
import { MOCK_TRAMITES_INICIADOS_TIPO } from './mockDataEjemplos';

const LABEL_POR_CODIGO = Object.fromEntries(
    TIPOS_TRAMITE_PICKER.map((t) => [t.nombreBackend, t.label.replace(/^Trámite /, '')])
);

const formatearTipoReal = (codigo) => LABEL_POR_CODIGO[codigo] || codigo;

/**
 * Trámites efectivamente iniciados, agrupados por el tipo REAL del trámite (no el
 * tipoTramite del turno que le dio origen — ver TramitesVolumenConversion, que sí
 * agrupa por turno). Responde: de los trámites que se abrieron, ¿en qué módulo
 * terminaron? Puede haber más categorías que tipos de turno (ej. un turno GENERAL
 * puede terminar en un PUAM o un Plan de Pago).
 * @param {{data: Array<{nombreTipoTramite: string, cantidad: number, porcentaje: number}>}} props
 */
const TramitesIniciadosPorTipo = ({ data, compacto = false }) => {
    const filas = useMemo(() => [...(data || [])].sort((a, b) => b.cantidad - a.cantidad), [data]);
    const total = useMemo(() => filas.reduce((acc, f) => acc + f.cantidad, 0), [filas]);
    const maxCantidad = useMemo(() => Math.max(1, ...filas.map((f) => f.cantidad)), [filas]);

    return (
        <Card variant="outlined" sx={{
            position: compacto ? 'static' : 'relative', border: compacto ? 'none' : '1px solid #e2e8f0',
            borderRadius: compacto ? 0 : '12px', boxShadow: 'none', padding: compacto ? 0 : '20px 22px',
        }}>
            {!compacto && (
                <>
                    <InfoGrafico
                        titulo="Trámites iniciados por tipo"
                        queMuestra="Te dice, de los trámites que efectivamente se abrieron en el período, en qué módulo terminaron — puede diferir del tipo de turno con el que llegó el cliente (ej. un turno GENERAL puede terminar en un Plan de Pago)."
                        ejemploVisual={<TramitesIniciadosPorTipo compacto data={MOCK_TRAMITES_INICIADOS_TIPO} />}
                        comoLeerlo={[
                            'Cada fila es un tipo real de trámite abierto, ordenado de mayor a menor cantidad.',
                            'El largo de la barra es relativo al tipo con más trámites del período (no a un total fijo).',
                            'El número en negro es la cantidad absoluta; el número entre paréntesis es el % sobre el total de trámites abiertos.',
                            'Todas las barras usan el mismo azul a propósito: acá el tipo de trámite no tiene un lado "bueno" o "malo", solo se compara volumen.',
                        ]}
                        analisis="RTI y ART concentran más de la mitad de los trámites abiertos (36.7% + 30%). Si ese patrón se repite mes a mes, sirve para planificar carga de trabajo por área — por ejemplo, anticipar que el equipo de RTI va a necesitar más horas de atención que el de Plan de Pago, que representa solo el 13.3%."
                    />
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 16, fontWeight: 700, color: COLOR.negro }}>Trámites iniciados por tipo</Typography>
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 12.5, color: COLOR.grisTexto, marginBottom: '16px' }}>
                        Agrupado por el <b>trámite que se abrió</b>, no por el turno: por eso los tipos pueden no coincidir
                        con «Volumen vs. conversión» (un turno de Jubilación puede terminar en un Plan de pago). Acá cuenta
                        cada trámite: si una atención abrió dos, suma dos.
                    </Typography>
                </>
            )}
            {total === 0 ? (
                <Typography sx={{ fontFamily: FUENTE, fontSize: 13, color: COLOR.grisTexto }}>
                    No hay trámites iniciados en el período seleccionado.
                </Typography>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {filas.map((f) => (
                        <Box key={f.nombreTipoTramite}>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                                <Typography sx={{ fontFamily: FUENTE, fontSize: 13, color: COLOR.negro }}>
                                    {formatearTipoReal(f.nombreTipoTramite)}
                                </Typography>
                                <Typography sx={{ fontFamily: FUENTE, fontSize: 13 }}>
                                    <b style={{ color: COLOR.negro }}>{f.cantidad.toLocaleString('es-AR')}</b>{' '}
                                    <span style={{ color: COLOR.grisTexto }}>({Number(f.porcentaje).toFixed(1)}%)</span>
                                </Typography>
                            </Box>
                            <Box sx={{ height: 8, borderRadius: 4, background: COLOR.gris, marginTop: '4px', overflow: 'hidden' }}>
                                <Box sx={{ width: `${(f.cantidad / maxCantidad) * 100}%`, height: '100%', background: COLOR.azul }} />
                            </Box>
                        </Box>
                    ))}
                    {!compacto && (
                        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', borderTop: '1px solid #E3E6EB', paddingTop: '10px' }}>
                            <Typography sx={{ fontFamily: FUENTE, fontSize: 13, fontWeight: 700, color: COLOR.negro }}>Total</Typography>
                            <Typography sx={{ fontFamily: FUENTE, fontSize: 13, fontWeight: 700, color: COLOR.negro }}>
                                {total.toLocaleString('es-AR')} trámites
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}
        </Card>
    );
};

export default TramitesIniciadosPorTipo;
